<script setup lang="ts">
import { computed } from 'vue';
import { useInstitutionStore } from '@entities/institution';
import { useProfileNavigation } from '@features/profile';

const config = useRuntimeConfig();
const storeInstitution = useInstitutionStore();
const institution = computed(() => storeInstitution.institution);
const legalInfo = computed(() => institution.value?.legal_information);
const branches = computed(() => storeInstitution.branches || []);

const urlSite = computed(() => institution.value?.frontend_url || config.public.baseUrl || '');
const operatorName = computed(() => legalInfo.value?.individual_entrepreneur || 'Оператор');
const inn = computed(() => legalInfo.value?.INN || '—');
const ogrn = computed(() => legalInfo.value?.OGRN || '—');
const legalAddress = computed(() => legalInfo.value?.legal_address || '—');

const email = computed(
  () =>
    institution.value?.legal_information_email ||
    (legalInfo.value as any)?.email ||
    '—',
);

const phone = computed(() => {
  return (
    institution.value?.legal_information_phone ||
    branches.value?.[0]?.phone ||
    (institution.value as any)?.phone ||
    '—'
  );
});

// Инициализируем навигацию назад для страниц профиля
const { useProfileBackNavigation } = useProfileNavigation();
useProfileBackNavigation();

/**
 * Своя мета вместо общей по сайту — иначе юридические страницы уходят в индекс
 * с title и description главной и конкурируют с ней как дубли.
 */
useSeoMeta({
  title: () =>
    institution.value?.name
      ? `Политика обработки персональных данных — ${institution.value.name}`
      : 'Политика обработки персональных данных',
  description: () =>
    institution.value?.name
      ? `Как «${institution.value.name}» обрабатывает и защищает персональные данные: какие данные собираются при заказе, зачем, сколько хранятся и как их удалить.`
      : 'Как обрабатываются и защищаются персональные данные: какие данные собираются при заказе, зачем, сколько хранятся и как их удалить.',
  ogTitle: () =>
    institution.value?.name
      ? `Политика обработки персональных данных — ${institution.value.name}`
      : 'Политика обработки персональных данных',
});
</script>

<template>
  <section class="privacy">
    <UContainer
      :ui="{
        base: 'mx-auto',
        padding: 'px-4 sm:px-6 lg:px-8',
        constrained: 'max-w-[1920px]',
      }"
    >
      <UiButtonBack to="/lk" />
      <h1>ПОЛИТИКА ОБРАБОТКИ ПЕРСОНАЛЬНЫХ ДАННЫХ</h1>
      <br />
      <br />
      <p>
        Настоящая Политика обработки персональных данных (далее — Политика) составлена в соответствии с требованиями
        Федерального закона от 27.07.2006 № 152-ФЗ «О персональных данных» (в ред. от 01.09.2025) и определяет порядок
        обработки персональных данных и меры по обеспечению их безопасности.
      </p>
      <br />

      <h4>Оператор персональных данных</h4>
      <br />
      <p>
        Оператором персональных данных является {{ operatorName }} (ИНН: {{ inn }}, ОГРНИП: {{ ogrn }}), заведение
        «{{ institution?.name }}».
      </p>
      <p>
        Адрес сайта: <a :href="urlSite" target="_blank">{{ urlSite }}</a><br />
        E-mail: <a :href="`mailto:${email}`">{{ email }}</a><br />
        Телефон: <a :href="`tel:${phone}`">{{ phone }}</a>
      </p>
      <br />

      <h4>1. Общие положения</h4>
      <br />
      <p>
        1.1. Оператор ставит своей важнейшей целью соблюдение прав и свобод человека и гражданина при обработке его
        персональных данных, в том числе защиты прав на неприкосновенность частной жизни, личную и семейную тайну.
        <br />
        1.2. Настоящая Политика применяется ко всей информации, которую Оператор может получить о посетителях сайта
        <a :href="urlSite" target="_blank">{{ urlSite }}</a>.
        <br />
        1.3. Согласие на обработку персональных данных даётся Пользователем отдельным действием — отметкой
        соответствующего поля при регистрации или входе. Согласие на получение рекламных сообщений даётся
        отдельно от него и не является условием оформления заказа. Дата, вид согласия и редакция настоящей
        Политики, действовавшая на этот момент, фиксируются Оператором.
      </p>
      <br />

      <h4>2. Основные понятия</h4>
      <br />
      <p>
        2.1. Автоматизированная обработка персональных данных — обработка с помощью средств вычислительной техники.
        <br />
        2.2. Блокирование персональных данных — временное прекращение обработки персональных данных (за исключением
        случаев, если обработка необходима для уточнения персональных данных).<br />
        2.3. Веб-сайт — совокупность графических и информационных материалов, а также программ для ЭВМ и баз данных,
        доступных по адресу <a :href="urlSite" target="_blank">{{ urlSite }}</a>.
        <br />
        2.4. Обезличивание персональных данных — действия, в результате которых невозможно определить без
        дополнительной информации принадлежность данных конкретному Пользователю.
        <br />
        2.5. Обработка персональных данных — любое действие (операция) или совокупность действий, совершаемых с
        персональными данными, включая сбор, запись, систематизацию, накопление, хранение, уточнение, извлечение,
        использование, передачу, обезличивание, блокирование, удаление, уничтожение.
        <br />
        2.6. Персональные данные — любая информация, относящаяся прямо или косвенно к определённому или определяемому
        Пользователю сайта.
        <br />
        2.7. Пользователь — любой посетитель сайта <a :href="urlSite" target="_blank">{{ urlSite }}</a>.
        <br />
        2.8. Трансграничная передача — передача персональных данных на территорию иностранного государства иностранному
        органу власти, иностранному физическому или юридическому лицу.
        <br />
        2.9. Уничтожение персональных данных — любые действия, в результате которых данные уничтожаются безвозвратно.
      </p>
      <br />

      <h4>3. Основные права и обязанности Оператора</h4>
      <br />
      <p>
        3.1. Оператор имеет право:<br />
        — получать от субъекта персональных данных достоверную информацию и/или документы, содержащие персональные
        данные;<br />
        — в случае отзыва субъектом согласия на обработку персональных данных продолжать обработку в случаях,
        предусмотренных законом № 152-ФЗ;<br />
        — самостоятельно определять состав и перечень мер защиты персональных данных.<br />

        <br />
        3.2. Оператор обязан:<br />
        — предоставлять субъекту персональных данных по его просьбе информацию, касающуюся обработки его данных;<br />
        — организовывать обработку персональных данных в порядке, установленном действующим законодательством РФ;<br />
        — отвечать на обращения и запросы субъектов персональных данных и их законных представителей в срок,
        установленный законом;<br />
        — публиковать настоящую Политику в открытом доступе на сайте;<br />
        — принимать правовые, организационные и технические меры для защиты персональных данных от неправомерного
        доступа;<br />
        — исполнять иные обязанности, предусмотренные законом № 152-ФЗ.
      </p>
      <br />

      <h4>4. Основные права и обязанности субъектов персональных данных</h4>
      <br />
      <p>
        4.1. Субъекты персональных данных имеют право:<br />
        — получать информацию об обработке своих персональных данных;<br />
        — требовать уточнения, блокирования или уничтожения персональных данных в случаях, предусмотренных законом;<br />
        — отзывать согласие на обработку персональных данных в любой момент;<br />
        — обжаловать неправомерные действия или бездействие Оператора в уполномоченный орган (Роскомнадзор) или в
        судебном порядке;<br />
        — на осуществление иных прав, предусмотренных законодательством РФ.<br />

        <br />
        4.2. Субъекты персональных данных обязаны:<br />
        — предоставлять Оператору достоверные данные о себе;<br />
        — сообщать Оператору об изменении своих персональных данных.<br />
        4.3. Лица, передавшие Оператору недостоверные сведения, несут ответственность в соответствии с
        законодательством РФ.
      </p>
      <br />

      <h4>5. Принципы обработки персональных данных</h4>
      <br />
      <p>
        5.1. Обработка персональных данных осуществляется на законной и справедливой основе.<br />
        5.2. Обработка ограничивается достижением конкретных, заранее определённых и законных целей (принцип
        минимизации данных).<br />
        5.3. Не допускается объединение баз данных, обработка которых осуществляется в целях, несовместимых между
        собой.<br />
        5.4. Обработке подлежат только данные, которые отвечают целям их обработки.<br />
        5.5. Хранение персональных данных осуществляется не дольше, чем этого требуют цели обработки.
      </p>
      <br />

      <h4>6. Цели обработки персональных данных</h4>
      <br />
      <p>
        6.1. Цели обработки:<br />
        — приём, исполнение и доставка заказа, связь по нему;<br />
        — регистрация и ведение учётной записи, вход в неё;<br />
        — программа лояльности: начисление и списание баллов, промокоды, подарки;<br />
        — поддержка и рассмотрение обращений;<br />
        — рекламные и информационные рассылки — только при отдельном согласии;<br />
        — защита от мошенничества и злоупотреблений (повторное получение бонусов, подбор пароля);<br />
        — исполнение требований законодательства, включая кассовую и бухгалтерскую отчётность.<br />
        6.2. Обрабатываемые персональные данные:<br />
        — фамилия и имя;<br />
        — номер телефона;<br />
        — электронный адрес (e-mail);<br />
        — дата рождения и пол — если Пользователь их указал;<br />
        — адреса доставки, включая комментарии к ним и координаты точки на карте;<br />
        — идентификаторы учётных записей во внешних сервисах, через которые выполняется вход:
        Яндекс ID, VK ID, Telegram, MAX;<br />
        — история заказов, платежей, начислений и списаний баллов;<br />
        — переписка и вложения в чате поддержки, оценки заказов и отзывы;<br />
        — технические данные: IP-адрес, файлы cookie, сведения об устройстве и браузере,
        технический идентификатор устройства, токен push-уведомлений, версия приложения;<br />
        — рекламные метки перехода на сайт (UTM).<br />
        6.3. Правовые основания:<br />
        — исполнение договора, стороной которого является субъект (п. 5 ч. 1 ст. 6 закона № 152-ФЗ), —
        для приёма, оплаты и доставки заказа;<br />
        — согласие субъекта (п. 1 ч. 1 ст. 6 закона № 152-ФЗ) — для рекламных рассылок
        и необязательных файлов cookie;<br />
        — исполнение обязанностей, возложенных законом (п. 2 ч. 1 ст. 6), — для кассовых чеков
        и бухгалтерского учёта;<br />
        — законные интересы Оператора (п. 7 ч. 1 ст. 6) — для защиты от мошенничества
        и злоупотреблений при получении бонусов.
      </p>
      <br />

      <h4>7. Условия обработки персональных данных</h4>
      <br />
      <p>
        7.1. Обработка персональных данных осуществляется только на основании отдельного согласия субъекта, исполнения
        договора или иных законных оснований, предусмотренных ст. 6 закона № 152-ФЗ.<br />
        7.2. В случаях, предусмотренных законом, Оператор вправе обрабатывать персональные данные без согласия
        субъекта.
      </p>
      <br />

      <h4>8. Порядок сбора, хранения и передачи персональных данных</h4>
      <br />
      <p>
        8.1. Оператор обеспечивает сохранность персональных данных и принимает все возможные меры, исключающие доступ
        неуполномоченных лиц, в том числе шифрование, двухфакторную аутентификацию, ограничение доступа.<br />
        8.2. Персональные данные передаются третьим лицам только в объёме, необходимом для исполнения заказа
        и работы сервиса, либо в случаях, предусмотренных законом. Обработку по поручению Оператора
        (ч. 3 ст. 6 закона № 152-ФЗ) осуществляют:<br />
        — платёжные сервисы — приём оплаты и формирование кассового чека;<br />
        — система автоматизации предприятия питания — передача состава заказа на кухню;<br />
        — служба веб-аналитики Яндекс.Метрика — статистика посещений, если Пользователь дал согласие
        на аналитические cookie;<br />
        — сервисы доставки push-уведомлений и мобильной аналитики — доставка сообщений в приложение.<br />
        8.3. В случае выявления неточностей в персональных данных, Пользователь может обновить их самостоятельно
        в личном кабинете либо направив уведомление на e-mail <a :href="`mailto:${email}`">{{ email }}</a>
        с пометкой «Актуализация персональных данных».<br />
        8.4. Базы данных, содержащие персональные данные граждан Российской Федерации, находятся на территории
        Российской Федерации в соответствии с требованиями ч. 5 ст. 18 закона № 152-ФЗ. Отдельные технические
        сервисы, указанные в п. 8.2 (доставка push-уведомлений, аналитика мобильного приложения), могут
        обрабатывать технические идентификаторы за пределами Российской Федерации.<br />
        8.5. Сроки хранения:<br />
        — данные заказов, платежей и кассовых документов — в течение срока, установленного налоговым
        и бухгалтерским законодательством;<br />
        — данные учётной записи — до её удаления Пользователем;<br />
        — после удаления учётной записи имя и фамилия удаляются в течение 30 дней; сведения о заказах,
        платежах и полученных бонусах сохраняются как первичные документы и для защиты от повторного
        получения бонусов при повторной регистрации.<br />
        8.6. Информация, собираемая сторонними сервисами, обрабатывается указанными лицами в соответствии с их
        политикой конфиденциальности. Оператор заключает с ними договоры, предусматривающие обязанность
        обработки персональных данных в соответствии с законом № 152-ФЗ.<br />
        8.7. Условием прекращения обработки персональных данных является достижение целей, отзыв согласия
        субъектом — в части обработки, которая ведётся на основании согласия, — или выявление неправомерной
        обработки. Отзыв согласия не прекращает обработку, которая ведётся на иных законных основаниях
        (исполнение договора, требования законодательства о хранении документов).
      </p>
      <br />

      <h4>9. Использование файлов cookie</h4>
      <br />
      <p>
        9.1. Сайт использует две категории файлов cookie:<br />
        — <b>строго необходимые</b> — вход в учётную запись, корзина, выбранный филиал, сохранение решения
        по cookie. Без них сайт не работает, поэтому они используются на основании исполнения договора
        и отключить их нельзя;<br />
        — <b>аналитические</b> — Яндекс.Метрика и собственная статистика сервиса. Используются только
        при согласии Пользователя.<br />
        9.2. Согласие на аналитические cookie даётся в баннере при первом посещении. Отказ равнозначен согласию
        и доступен в один клик: кнопка «Только необходимые». До получения согласия аналитические сервисы
        не подключаются.<br />
        9.3. Ранее данное решение можно изменить в любой момент — ссылка «Настройки cookie» в нижней части сайта
        либо переключатель «Аналитика» в личном кабинете, раздел «Безопасность». Отзыв действует с момента
        изменения настройки.<br />
        9.4. В мобильном приложении сторонние сервисы веб-аналитики не используются, поэтому баннер о cookie
        не показывается. Собственная статистика использования приложения ведётся Оператором на основании его
        законного интереса (п. 7 ч. 1 ст. 6 закона № 152-ФЗ) — понимать, какими разделами пользуются и где
        прерывается оформление заказа. Отключить её можно там же: личный кабинет → «Безопасность» →
        «Аналитика».<br />
        9.5. Пользователь также вправе отключить cookie в настройках браузера, однако это может повлечь нарушение
        работы отдельных функций сайта.
      </p>
      <br />

      <h4>10. Трансграничная передача персональных данных</h4>
      <br />
      <p>
        10.1. Основные базы данных сервиса размещены на территории Российской Федерации (п. 8.4).<br />
        10.2. При использовании мобильного приложения технические идентификаторы устройства и токены
        push-уведомлений передаются сервисам доставки уведомлений и мобильной аналитики, инфраструктура
        которых находится за пределами Российской Федерации. Такая передача необходима для работы
        push-уведомлений о статусе заказа.<br />
        10.3. Трансграничная передача осуществляется с соблюдением требований ст. 12 закона № 152-ФЗ.
      </p>
      <br />

      <h4>11. Как воспользоваться своими правами</h4>
      <br />
      <p>
        11.1. <b>Изменить свои данные</b> — в личном кабинете, раздел «Личная информация».<br />
        11.2. <b>Отказаться от рекламных сообщений</b> — в личном кабинете, раздел «Безопасность»,
        переключатель «Акции и предложения». Уведомления о статусе собственного заказа при этом продолжают
        приходить: они не являются рекламой.<br />
        11.3. <b>Изменить решение по cookie и аналитике</b> — ссылка «Настройки cookie» в нижней части сайта
        либо переключатель «Аналитика» в личном кабинете, раздел «Безопасность». В мобильном приложении —
        только переключатель.<br />
        11.4. <b>Удалить учётную запись</b> — в личном кабинете, кнопка «Удалить профиль». Вход
        в аккаунт станет невозможен, накопленные баллы сгорают, имя и фамилия удаляются в течение 30 дней.
        Сведения о заказах и платежах сохраняются в объёме, необходимом для отчётности (п. 8.5).<br />
        11.5. <b>Запросить информацию об обработке своих данных</b> или направить иное требование —
        по контактам из раздела 13. Ответ направляется в срок, установленный законом.
      </p>
      <br />

      <h4>12. Конфиденциальность персональных данных</h4>
      <br />
      <p>
        12.1. Оператор и иные лица, получившие доступ к персональным данным, обязаны не раскрывать их третьим лицам
        без согласия субъекта, если иное не предусмотрено федеральным законом.
      </p>
      <br />

      <h4>13. Заключительные положения</h4>
      <br />
      <p>
        13.1. Пользователь может получить разъяснения по обработке его персональных данных, направив запрос:<br />
        — e-mail: <a :href="`mailto:${email}`">{{ email }}</a><br />
        — телефон: <a :href="`tel:${phone}`">{{ phone }}</a><br />
        13.2. Данный документ будет обновляться по мере изменения требований законодательства. Политика действует
        бессрочно до замены её новой версией.<br />
        13.3. Актуальная версия документа размещена по адресу:
        <a :href="`${urlSite}/privacy`" target="_blank">{{ `${urlSite}/privacy` }}</a>
      </p>
      <br />

      <h4>Контактные данные Оператора</h4>
      <br />
      <p>
        Оператор: {{ operatorName }}<br />
        ИНН: {{ inn }}<br />
        ОГРНИП: {{ ogrn }}<br />
        Адрес: {{ legalAddress }}<br />
        E-mail: <a :href="`mailto:${email}`">{{ email }}</a><br />
        Телефон: <a :href="`tel:${phone}`">{{ phone }}</a><br />
        Сайт: <a :href="urlSite" target="_blank">{{ urlSite }}</a>
      </p>

      <p>Дата последнего обновления: 30 августа 2026 г.</p>
    </UContainer>
  </section>
</template>

<style scoped lang="scss">
.privacy {
  &__back {
    @apply p-4 pl-0 py-7 bg-white text-black dark:bg-transparent dark:text-white rounded-full flex items-center;
  }
}
</style>
